<template>
    <div class="swipe-container" :class="{'swiped': swipe_moving || swipe_full}">
      <div ref="swipe_item_element" class="swipe_element" :style="'transform: translateX(' + swipe_position + 'px)'">
        <slot name="swipe-element"> </slot>
      </div>

      <div ref="swipe_actions_left_element" class="swipe-actions-left">
        <slot name="swipe-actions-left"></slot>
      </div>

      <div ref="swipe_actions_right_element" class="swipe-actions-right">
        <slot name="swipe-actions-right"></slot>
      </div>
    </div>
</template>

<script setup>
  import {onMounted, onBeforeUnmount, ref } from 'vue';

  const swipe_item_element          = ref();
  const swipe_actions_right_element = ref();
  const swipe_actions_left_element  = ref();

  const global_listeners     = ref(false);
  const local_listeners      = ref(false);

  const touch_prevention     = ref(false);
  const touch_position       = ref({
    x : 0,
    y : 0,
  });
  const swipe_full           = ref(false);
  const swipe_moving         = ref(false);
  const swipe_position       = ref(0);
  const swipe_direction      = ref();
  const swipe_max_width      = ref({
    'right' : 0,
    'left'  : 0,
  });

  const threshold = {
    x : 0,
    y : 0,
  }

  onMounted(() => {
    setSwipeMaxWidth();
    swipe_item_element.value.addEventListener('touchstart' , onTouchStart, {passive: true});

  })

  onBeforeUnmount(() => {
    swipe_item_element.value.removeEventListener('touchstart' , onTouchStart);
    unsetLocalListeners();
    unsetGlobalListeners();
  })

  const onTouchStart = (event) => {
    setTouchPrevention();
    setLocalListeners();
    setTouchPosition(event.touches[0].pageX, event.touches[0].pageY);
  }

  const onTouchMove = (event) => {
    setThreshold(
      Math.abs(touch_position.value.x - event.touches[0].pageX),
      Math.abs(touch_position.value.y - event.touches[0].pageY)
    )

    if (threshold.x < threshold.y + 7) {
      return;
    }

    setSwipeMoving(true);
    swipeElement(event.touches[0].pageX, event.touches[0].pageY);
  }

  const onTouchEnd = (event) => {
    unsetLocalListeners();

    if (swipe_moving.value === false) {
      return;
    }

    if (swipe_full.value === false) {
      swipeElementClose();
      return;
    }

    setGlobalListeners();
    setThreshold(0,0);
  }

  const onGlobalClick = (event) => {
    if (touch_prevention.value === true) {
      unsetTouchPrevention();
      return;
    }

    if (swipe_full.value === false) {
      return;
    }

    swipeElementClose();
    unsetLocalListeners();
  }

  const swipeElement = (x, y) => {
    let direction = (x > touch_position.value.x) ? 'right' : 'left';

    if (swipe_full.value === true && swipe_direction.value === direction) {
      setTouchPosition(x, y);
      return;
    }

    if (swipe_direction.value !== direction) {
      setTouchPosition(x, y);
      setSwipeFull(false);
    }

    setSwipeDirection(direction);
    setSwipePosition(swipe_position.value + (x - touch_position.value.x));
    setTouchPosition(x, y);

    if (Math.abs(swipe_position.value) > swipe_max_width.value[direction]) {
      setSwipePosition(swipe_position.value > 0 ? swipe_max_width.value[direction] : -(swipe_max_width.value[direction]));
      setSwipeFull(true);
    }
  }

  const setThreshold = (x, y) => {
    threshold.x = x;
    threshold.y = y;
  }

  const swipeElementClose = () => {
    setSwipePosition(0);
    setSwipeFull(false);
    setSwipeMoving(false);
  }

  /***
   * This is currently bugged on cellphones
   */
  const swipeElementCloseAnimate = () => {
    let timer = 1;

    let interval = setInterval(() => {
      timer = transitionEaseIn(timer);
      setSwipePosition(swipe_position.value > 0 ? swipe_position.value - 1 : swipe_position.value + 1)

      if (Math.abs(swipe_position.value) <= 0 ) {
        setSwipePosition(0);
        setSwipeFull(false);
        setSwipeMoving(false);
        clearInterval(interval);
      }
    }, timer)
  }

  const setTouchPosition = (x, y) => {
    touch_position.value.x = x;
    touch_position.value.y = y;
  }

  const setSwipeMaxWidth = () => {
    swipe_max_width.value.right = swipe_actions_right_element.value.offsetWidth;
    swipe_max_width.value.left  = swipe_actions_left_element.value.offsetWidth;
  }

  const setSwipeDirection = (direction) => {
    swipe_direction.value = direction;
  }

  const setSwipeFull = (full) => {
    swipe_full.value = full;
  }

  const setSwipePosition = (position) => {
    swipe_position.value = position;
  }

  const setSwipeMoving = (moving) => {
    swipe_moving.value = moving;
  }

  const setTouchPrevention = () => {
    touch_prevention.value = true;
  }

  const unsetTouchPrevention = () => {
    touch_prevention.value = false;
  }

  const setLocalListeners = () => {
    swipe_item_element.value.addEventListener('touchend'   , onTouchEnd);
    swipe_item_element.value.addEventListener('touchmove'  , onTouchMove);
    local_listeners.value = true;
  }

  const unsetLocalListeners = () => {
    if (local_listeners.value === false) {
      return;
    }

    swipe_item_element.value.removeEventListener('touchend'   , onTouchEnd);
    swipe_item_element.value.removeEventListener('touchmove'  , onTouchMove);
    local_listeners.value = false;
  }

  const setGlobalListeners = () => {
    window.addEventListener('touchstart', onGlobalClick, {passive: true});
    global_listeners.value = true;
  }

  const unsetGlobalListeners = () => {
    if (global_listeners.value === false) {
      return;
    }

    window.removeEventListener('touchstart', onGlobalClick);
    global_listeners.value = true;
  }

  const transitionEaseIn = (x) => {
    return x * x * x * x * x;
  }
</script>